// All parts of a remote character are sampled at the same presentation time.
// Keep the existing packet format, including its wrapping eight-bit sequence.
export const isNewerSequence = (id, previous) => previous < 0 || ((id - previous + 256) % 256 > 0 && (id - previous + 256) % 256 < 128);
export const angleDifference = (from, to) => ((to - from + 180) % 360 + 360) % 360 - 180;
export const isDestroyedPose = pose => pose.x === 133.7 && pose.y === 133.7 && pose.r === 360;

export class SyncObject {
	constructor(x = 0, y = 0, r = 0) {
		Object.assign(this, {x, y, r, mirror: false, serverId: -1, serverTime: 0, destroyed: false});
		this.serverPos = {x, y, r};
		this.snapshots = [];
	}

	forcePosition(id, x, y, r, mirror = this.mirror, time = performance.now()) {
		this.snapshots.length = 0;
		this.serverId = id;
		this.serverTime = time;
		this.serverPos = {x, y, r, mirror, time};
		this.snapshots.push(this.serverPos);
		this.apply(this.serverPos);
	}

	updateServerPosition(id, x, y, r, time, mirror = this.mirror) {
		if (!isNewerSequence(id, this.serverId)) return;
		if (!this.snapshots.length) return this.forcePosition(id, x, y, r, mirror, time);
		this.serverId = id;
		this.serverTime = time;
		this.serverPos = {x, y, r, mirror, time: Math.max(time, this.serverPos.time + 0.01)};
		this.snapshots.push(this.serverPos);
		if (this.snapshots.length > 32) this.snapshots.shift();
	}

	apply(pose) {
		this.destroyed = isDestroyedPose(pose);
		this.mirror = pose.mirror;
		// A destroyed torso remains the coordinate origin for detached limbs.
		if (!this.destroyed) {
			this.x = pose.x;
			this.y = pose.y;
			this.r = pose.r;
		}
	}

	interpolatePosition(time = performance.now() - 100, maxExtrapolation = 100) {
		const frames = this.snapshots;
		if (!frames.length) return;
		while (frames.length > 2 && frames[1].time <= time) frames.shift();
		const a = frames[0];
		const b = frames[1];
		if (!b || time <= a.time) return this.apply(a);
		// Discrete events switch together at the packet's presentation time.
		if (a.mirror !== b.mirror || isDestroyedPose(a) || isDestroyedPose(b)) {
			return this.apply(time < b.time ? a : b);
		}
		const duration = b.time - a.time;
		const fraction = (Math.min(time, b.time + maxExtrapolation) - a.time) / duration;
		// Continuous samples are known to be alive. Write directly instead of
		// allocating two temporary pose objects per part on every render frame.
		this.destroyed = false;
		this.mirror = b.mirror;
		this.x = a.x + (b.x - a.x) * fraction;
		this.y = a.y + (b.y - a.y) * fraction;
		this.r = a.r + angleDifference(a.r, b.r) * fraction;
	}
}

// Plays a remote player's poses on the sender's own clock, a buffer behind the
// newest packet. Network jitter then delays poses instead of changing their speed.
export class PlaybackClock {
	constructor({minDelay = 80, maxDelay = 250} = {}) {
		this.minDelay = minDelay;
		this.maxDelay = maxDelay;
		this.offset = null;
		this.lateness = 0;
		this.delay = minDelay;
		this.renderTime = null;
		this.localTime = null;
	}

	// Learns the clock offset and jitter from a packet the sender stamped with senderTime.
	receive(senderTime, localTime) {
		const sample = localTime - senderTime;
		// The fastest packet sets the offset; creep up slowly to follow clock drift.
		if (this.offset === null || sample < this.offset) this.offset = sample;
		else this.offset += 0.02;
		this.lateness = Math.max(sample - this.offset, this.lateness * 0.995);
		this.delay = Math.min(this.maxDelay, this.minDelay + this.lateness);
	}

	// The sender time to show now. It runs at most 10% fast or slow while it settles.
	time(localTime) {
		const target = localTime - this.offset - this.delay;
		if (this.renderTime === null || Math.abs(target - this.renderTime) > 500) {
			this.renderTime = target;
		} else {
			const elapsed = Math.max(0, localTime - this.localTime);
			const next = this.renderTime + elapsed;
			const correction = Math.max(-elapsed * 0.1, Math.min(elapsed * 0.1, target - next));
			this.renderTime = Math.max(this.renderTime, next + correction);
		}
		this.localTime = localTime;
		return this.renderTime;
	}
}
